import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { CheckItem } from "../utilities/checkItems";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { safeLocale } from "@/utilities/i18n/safeLocale";

const ICON_PROPS = { fontSize: "small", sx: { mt: 0.55, flexShrink: 0 } } as const;

export function ChecklistLine({ item, pending }: { item: CheckItem; pending: boolean }) {
  const { t, i18n } = useTranslation();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        alignItems: "center",
        gap: 0.75,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0, pr: 1 }}>
        <StatusIcon item={item} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              textAlign: "left",
              wordBreak: "break-word",
              color: item.found ? "text.primary" : "text.secondary",
            }}
          >
            {item.label}
          </Typography>
          {item.diagnosticId && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                textAlign: "left",
                wordBreak: "break-all",
                lineHeight: 1.3,
                color: "text.secondary",
              }}
            >
              {item.diagnosticId}
            </Typography>
          )}
        </Box>
      </Box>
      <Tooltip
        title={
          item.valueHint || item.dateTime ? (
            <>
              {item.valueHint && <div>{item.valueHint}</div>}
              {item.dateTime && (
                <div>
                  {t("liveData.checklist.reportedAt", {
                    date: formatDate(item.dateTime, i18n.language, { seconds: true }),
                  })}
                </div>
              )}
            </>
          ) : (
            ""
          )
        }
        placement="left"
      >
        <Typography
          variant="body2"
          sx={{
            textAlign: "right",
            wordBreak: "break-word",
            color: item.stale ? "text.disabled" : item.found ? "text.primary" : "text.secondary",
            pl: 1,
            ...(item.valueHint && {
              justifySelf: "end",
              cursor: "help",
              textDecoration: "underline dotted",
              textUnderlineOffset: 3,
            }),
          }}
        >
          {item.value ||
            (pending
              ? t("liveData.checklist.loading")
              : t("liveData.checklist.notFound"))}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function StatusIcon({ item }: { item: CheckItem }) {
  const { t, i18n } = useTranslation();

  if (!item.found) {
    return <HelpOutlineIcon color="disabled" {...ICON_PROPS} />;
  }

  if (item.mismatch) {
    return (
      <Tooltip title={t("liveData.checklist.odometerMismatchHint", { gap: item.mismatch })}>
        <ErrorOutlineIcon color="error" {...ICON_PROPS} />
      </Tooltip>
    );
  }

  const icon = item.stale ? (
    <HistoryIcon color="error" {...ICON_PROPS} />
  ) : item.fromFallback ? (
    <HistoryIcon color="warning" {...ICON_PROPS} />
  ) : (
    <CheckCircleOutlineIcon color="success" {...ICON_PROPS} />
  );

  // A figure entered by hand says so whatever its age, so nobody takes it for a vehicle report.
  const title = item.manual
    ? t("liveData.checklist.manualOdometerHint", {
        date: item.lastReportedAt?.toLocaleDateString(safeLocale(i18n.language), {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
      })
    : item.stale
      ? t("liveData.checklist.staleHint", {
          date: formatDate(item.lastReportedAt, i18n.language),
        })
      : item.fromFallback
        ? item.lastReportedAt
          ? t("liveData.checklist.beforePeriodHint", {
              date: formatDate(item.lastReportedAt, i18n.language),
            })
          : t(
              "liveData.checklist.fromHistoryHint",
              "Not reported live — this value is from the last 24 hours",
            )
        : undefined;

  return title ? <Tooltip title={title}>{icon}</Tooltip> : icon;
}
